import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { ErrorBox, useApi } from './kit';

// F35 "people and channels": at each company the best 1-3 people, each with the channel Outlevo proposes. The person
// unticks people or changes a channel, then presses Add; nothing is added before that, and nothing is sent.

type Channel = 'email' | 'linkedin' | 'email_linkedin';
type Person = { contact_id: string; name: string; title: string | null; why: string | null; relevance?: number | null; verified_email: boolean; linkedin: boolean; channel: Channel | null; unreachable_reason: string | null; email_to_verify: boolean; added_channel?: Channel | null };
type Company = { company_id: string; name: string; domain: string; note: string | null; total: number; people: Person[] };

const LABEL: Record<Channel, string> = { email: 'Email', linkedin: 'LinkedIn', email_linkedin: 'Email, then LinkedIn' };
const options = (p: Person): Channel[] => (p.channel === 'email_linkedin' ? ['email_linkedin', 'email', 'linkedin'] : p.channel ? [p.channel] : []);

export function PeopleCard({ campaignId, companyIds, title, done, onDone }: { campaignId: string; companyIds: string[]; title: string; done: { text: string } | null; onDone: (text: string) => Promise<void> }) {
  const [all, setAll] = useState(false);
  const r = useApi<{ data: Company[] }>(`/v1/launch/people?company_ids=${companyIds.join(',')}&per_company=${all ? 10 : 3}&campaign_id=${campaignId}`);
  // a weak fit for the buyer roles starts unticked (a product designer was proposed and ticked, 2026-10-30)
  const weak = (p: Person) => typeof p.relevance === 'number' && p.relevance < 0.5;
  const [pick, setPick] = useState<Record<string, Channel | null>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  // everyone reachable starts ticked with Outlevo's channel
  useEffect(() => {
    if (!r.data) return;
    // the first three per company start ticked; more shown with "Show all" start unticked
    // once added, the card shows what was added (it showed the defaults again after a reload)
    setPick((cur) => { const n = { ...cur }; for (const c of r.data!.data) c.people.forEach((p, i) => { if (!(p.contact_id in n)) n[p.contact_id] = done ? p.added_channel ?? null : p.channel && i < 3 && !weak(p) && !Object.keys(cur).length ? p.channel : null; }); return n; });
  }, [r.data]);
  const chosen = Object.entries(pick).filter((e): e is [string, Channel] => Boolean(e[1]));
  const people = r.data?.data.flatMap((c) => c.people) ?? [];
  const toVerify = chosen.filter(([id, ch]) => ch !== 'linkedin' && people.find((p) => p.contact_id === id)?.email_to_verify).length;
  const more = r.data?.data.some((c) => c.total > c.people.length) ?? false;
  const add = async () => {
    setErr(null); setBusy(true);
    try {
      const out = await api<{ added: number; verifying: number; data: Array<{ added: boolean; reason?: string }> }>(`/v1/campaigns/${campaignId}/people`, { method: 'POST', body: { people: chosen.map(([contact_id, channel]) => ({ contact_id, channel })) } });
      const left = out.data.filter((x) => !x.added).length;
      await onDone(`Added ${out.added} ${out.added === 1 ? 'person' : 'people'} with their channels${out.verifying ? `; ${out.verifying} address${out.verifying === 1 ? ' is' : 'es are'} being checked first` : ''}${left ? `; ${left} could not be added` : ''}`);
    } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  return (
    <div className="wg wg-people" role="group" aria-label={title}>
      <div className="wg-title">{title}</div>
      {r.error ? <ErrorBox error={r.error} /> : !r.data ? <p className="muted small" role="status">Reading the people…</p> : (
        <>
          {r.data.data.map((c) => (
            <section key={c.company_id} className="pp-company" aria-label={c.name}>
              <b>{c.name}</b> <span className="muted small">{c.domain}{c.total > c.people.length ? ` · best ${c.people.length} of ${c.total}` : ''}</span>
              {c.people.length ? (
                <ul className="pp-people">{c.people.map((p) => (
                  <li key={p.contact_id} className={p.channel ? '' : 'muted'}>
                    <label>
                      <input type="checkbox" disabled={Boolean(done) || !p.channel} checked={Boolean(pick[p.contact_id])}
                        onChange={(e) => setPick({ ...pick, [p.contact_id]: e.target.checked ? p.channel : null })} aria-label={`Include ${p.name}`} />
                      <span><b>{p.name}</b>{p.title ? `, ${p.title}` : ''}{p.why ? <span className="muted small"> · {p.why}</span> : null}
                        {p.channel && p.channel !== 'linkedin' && p.email_to_verify ? <span className="muted small"> · email checked before any send</span> : null}
                        {p.channel === 'linkedin' ? <span className="muted small"> · no email address found</span> : null}
                        {weak(p) && !done ? <span className="muted small"> · a weak fit for your buyer roles</span> : null}</span>
                    </label>
                    {done ? <span className="chip">{p.added_channel ? `Added · ${LABEL[p.added_channel]}` : 'Not added'}</span> : p.channel ? (
                      options(p).length > 1 ? (
                        <select aria-label={`Channel for ${p.name}`} disabled={Boolean(done) || !pick[p.contact_id]} value={pick[p.contact_id] ?? p.channel}
                          onChange={(e) => setPick({ ...pick, [p.contact_id]: e.target.value as Channel })}>
                          {options(p).map((o) => <option key={o} value={o}>{LABEL[o]}</option>)}
                        </select>
                      ) : <span className="chip">{LABEL[p.channel]}</span>
                    ) : <span className="small">{p.unreachable_reason}</span>}
                  </li>
                ))}</ul>
              ) : <p className="muted small">{c.note}</p>}
            </section>
          ))}
          {done ? <p className="wg-done" role="status">✓ {done.text}</p> : (
            <div className="wg-actions">
              <button type="button" className="btn btn-primary" disabled={busy || !chosen.length} onClick={() => void add()}>{busy ? 'Adding…' : `Add ${chosen.length} ${chosen.length === 1 ? 'person' : 'people'}${toVerify ? ` · ${toVerify} credit${toVerify === 1 ? '' : 's'}` : ''}`}</button>
              {more && !all ? <button type="button" className="btn" onClick={() => setAll(true)}>Show everyone found</button> : null}
              <span className="muted small">{toVerify ? `${toVerify} address${toVerify === 1 ? ' is' : 'es are'} checked first (1 credit each). ` : ''}Nothing is sent: emails wait for your review, LinkedIn notes for you to send.</span>
            </div>
          )}
        </>
      )}
      {err ? <ErrorBox error={err} /> : null}
    </div>
  );
}
